import {
  type AiCapabilities,
  type AiRunRecord,
  type AiRunSummary,
  type CommandDraftView,
  type AiMemoryNote,
  type AiSuggestionView,
  type AiUsageReport,
  type CreateMemoryNoteBody,
  type MemoryScope,
  type ProjectAiKeyView,
  type ProjectAiSpend,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const aiApi = {
  /**
   * Trạng thái AI + đường làm thủ công — ADR-006.
   *
   * ⚠️ Luôn 200, kể cả khi chưa ai nhập key. Đừng bọc lời gọi này trong
   * `try/catch` để "phòng khi AI chưa bật": trạng thái đó nằm trong `enabled`,
   * còn lỗi ở đây là lỗi thật.
   */
  capabilities: (projectId?: string) =>
    apiFetch<AiCapabilities>(
      // `projectId` có thì câu trả lời tính cả key của dự án — nếu không, thành
      // viên chưa nhập key riêng thấy mọi nút AI tắt trong một dự án đã gắn key,
      // trong khi lời gọi thật vẫn chạy (docs/plan-project-ai-key.md).
      projectId
        ? `/ai/capabilities?projectId=${encodeURIComponent(projectId)}`
        : '/ai/capabilities',
    ),

  /** Key AI của dự án — docs/plan-project-ai-key.md. */
  projectKey: (projectId: string) => apiFetch<ProjectAiKeyView>(`/projects/${projectId}/ai-key`),

  attachProjectKey: (projectId: string, keyId: string) =>
    apiFetch<ProjectAiKeyView>(`/projects/${projectId}/ai-key`, {
      method: 'POST',
      body: { keyId },
    }),

  detachProjectKey: (projectId: string) =>
    apiFetch<ProjectAiKeyView>(`/projects/${projectId}/ai-key`, { method: 'DELETE' }),

  /** Tiền AI dự án đã tiêu (USD) — mở cho `budget:read`. */
  projectAiSpend: (projectId: string) =>
    apiFetch<ProjectAiSpend>(`/projects/${projectId}/ai-spend`),

  runSkill: (
    name: string,
    body: { projectId: string; prompt: string; params?: unknown; runId?: string },
  ) => apiFetch<AiSuggestionView>(`/ai/skills/${name}/run`, { method: 'POST', body }),

  /** Bản nháp lệnh — APM-AI-l/m. Thân chỉ có chỉ số bị loại; server dựng lại lệnh. */
  commandDraft: (id: string) => apiFetch<CommandDraftView>(`/ai/command-drafts/${id}`),
  applyCommandDraft: (id: string, exclude: number[]) =>
    apiFetch<CommandDraftView>(`/ai/command-drafts/${id}/apply`, {
      method: 'POST',
      body: { exclude },
    }),

  /**
   * Những lượt chạy **của chính mình**, mới nhất trước — APM-UX-ai1.
   *
   * Khác `/ai/harness/runs` (cả tổ chức, đòi `ai_harness:use`): cửa này trả lời
   * "tôi đã nhờ trợ lý làm những gì", nên ai đăng nhập cũng gọi được.
   */
  myRuns: (limit = 20) => apiFetch<AiRunSummary[]>(`/ai/runs?limit=${limit}`),

  /**
   * Dấu vết **bền** của một lượt chạy — sống qua restart, khác `/ai/runs/:id`
   * (bản trong RAM, mất khi API khởi động lại). Chủ run đọc được run của mình.
   */
  runRecord: (runId: string) => apiFetch<AiRunRecord>(`/ai/harness/runs/${runId}`),

  /**
   * Chạy **tiếp** một lượt đã hỏng — APM-UX-ai3. Dùng lại cùng `runId` nên dấu
   * vết đọc liền một mạch; kết quả về `ai_suggestion` như một lượt bình thường.
   */
  resumeRun: (runId: string) =>
    apiFetch<AiSuggestionView>(`/ai/runs/${runId}/resume`, { method: 'POST' }),

  suggestions: (projectId: string) =>
    apiFetch<AiSuggestionView[]>(`/ai/suggestions?projectId=${projectId}`),

  review: (id: string, decision: 'accept' | 'reject', reason?: string) =>
    apiFetch<AiSuggestionView>(`/ai/suggestions/${id}/review`, {
      method: 'POST',
      body: { decision, reason },
    }),

  // Ghi chú bộ nhớ AI — APM-H-j.
  memoryNotes: (scope: MemoryScope, scopeId?: string) =>
    apiFetch<AiMemoryNote[]>(
      `/ai/memory-notes?scope=${scope}${scopeId ? `&scopeId=${scopeId}` : ''}`,
    ),
  createMemoryNote: (body: CreateMemoryNoteBody) =>
    apiFetch<AiMemoryNote>('/ai/memory-notes', { method: 'POST', body }),
  deleteMemoryNote: (id: string) => apiFetch<void>(`/ai/memory-notes/${id}`, { method: 'DELETE' }),
  confirmMemoryNote: (id: string) =>
    apiFetch<AiMemoryNote>(`/ai/memory-notes/${id}/confirm`, { method: 'POST' }),

  usage: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString();
    return apiFetch<AiUsageReport>(`/admin/ai/usage${query ? `?${query}` : ''}`);
  },

  /** Link tải CSV — trình duyệt tự tải, không đi qua `apiFetch`. */
  csvUrl: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString();
    return `/api/v1/admin/ai/usage.csv${query ? `?${query}` : ''}`;
  },
};

export const aiKeys = {
  usage: (from?: string, to?: string) => ['ai', 'usage', from, to] as const,
  capabilities: (projectId?: string) => ['ai', 'capabilities', projectId ?? 'me'] as const,
  projectKey: (projectId: string) => ['ai', 'project-key', projectId] as const,
  projectAiSpend: (projectId: string) => ['ai', 'project-spend', projectId] as const,
  suggestions: (projectId: string) => ['ai', 'suggestions', projectId] as const,
  myRuns: (limit: number) => ['ai', 'my-runs', limit] as const,
  runRecord: (runId: string) => ['ai', 'run-record', runId] as const,
  memoryNotes: (scope: MemoryScope, scopeId?: string) =>
    ['ai', 'memory-notes', scope, scopeId ?? ''] as const,
};
